import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import { defaultSYS2020_R01Dto, SYS2020_R01Dto } from './Dto_SYS2020_R01';

export interface SYS2020_R01Filter {
	commentCd: string;
	description: string;
}

export interface Store_SYS2020_R01 {
	SYS2020_R01Filter: SYS2020_R01Filter;
	SYS2020_R01PastFilter: SYS2020_R01Filter;
	setSYS2020_R01Filter: (filter: Partial<SYS2020_R01Filter>) => void;
	setSYS2020_R01PastFilter: (filter: Partial<SYS2020_R01Filter>) => void;

	SYS2020_R01List: SYS2020_R01Dto[];
	setSYS2020_R01List: (list: SYS2020_R01Dto[]) => void;
	insertSYS2020_R01List: (data: SYS2020_R01Dto[], index?: number) => void;
	removeSYS2020_R01List: (valueIndex: number) => void;
	updateSYS2020_R01List: (valueIndex: number, data: SYS2020_R01Dto) => void;

	deletedSYS2020_R01List: string[];
	addDeletedSYS2020_R01List: (idList: string[]) => void;
	removeDeletedSYS2020_R01List: (idList?: string[]) => void;

	selectedSYS2020_R01List: string[];
	setSelectedSYS2020_R01List: (idList: string[]) => void;
	addSelectedSYS2020_R01List: (idList: string[]) => void;
	removeSelectedSYS2020_R01List: (idList?: string[]) => void;
	initSelectedSYS2020_R01List: () => void;
	allSelectedSYS2020_R01List: () => void;

	originalSYS2020_R01List: SYS2020_R01Dto[];
	setOriginalSYS2020_R01List: (list: SYS2020_R01Dto[]) => void;

	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;

	allCount: number;
	setAllCount: (allCount: number) => void;

	initData: () => void;
}

export const defaultSYS2020_R01Filter: SYS2020_R01Filter = {
	commentCd: '',
	description: '',
};

export const useSYS2020_R01Store = create<Store_SYS2020_R01>((set) => ({
	SYS2020_R01Filter: defaultSYS2020_R01Filter,
	SYS2020_R01PastFilter: defaultSYS2020_R01Filter,
	setSYS2020_R01Filter: (filter) => set((state) => ({
		SYS2020_R01Filter: { ...state.SYS2020_R01Filter, ...filter },
	})),
	setSYS2020_R01PastFilter: (filter) => set((state) => ({
		SYS2020_R01PastFilter: { ...state.SYS2020_R01PastFilter, ...filter },
	})),

	SYS2020_R01List: [],
	setSYS2020_R01List: (SYS2020_R01List) => set((state) => {
		let newList: SYS2020_R01Dto[] = [];
		let valueAndOrderIndex = 1;
		SYS2020_R01List.forEach((item) => {
			newList.push({
				...defaultSYS2020_R01Dto,
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: Math.max(item.orderIndexSeq, 0),
			});
			valueAndOrderIndex++;
		});
		return { ...state, SYS2020_R01List: newList };
	}),

	insertSYS2020_R01List: (data, index) => set((state) => {
		let newList = [...state.SYS2020_R01List];
		const targetRowData = newList.find((item) => item.valueIndex === index);
		const targetOrderIndex = targetRowData?.orderIndex || 0;
		const newOrderIndexSeq = (targetRowData?.orderIndexSeq || 0) + 1;
		let maxValueIndex = Math.max(0, ...newList.map((item) => item.valueIndex || 0)) + 1;

		const targetRows = newList.filter((item) => item.orderIndex === targetOrderIndex);
		targetRows.forEach((item) => {
			if (item.orderIndexSeq >= newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq + 1;
			}
		});

		newList = newList.filter((item) => item.orderIndex !== targetOrderIndex);
		newList.push(...targetRows);

		const newData = data.map((item) => ({
			...defaultSYS2020_R01Dto,
			...item,
			valueIndex: maxValueIndex++,
			orderIndex: targetOrderIndex,
			orderIndexSeq: newOrderIndexSeq,
		}));

		if (index !== undefined) {
			newList.splice(index, 0, ...newData);
		} else {
			newList.push(...newData);
		}

		return { ...state, SYS2020_R01List: newList };
	}),

	removeSYS2020_R01List: (valueIndex) => set((state) => ({
		...state,
		SYS2020_R01List: state.SYS2020_R01List.filter((item) => item.valueIndex !== valueIndex),
	})),

	updateSYS2020_R01List: (valueIndex, data) => set((state) => ({
		...state,
		SYS2020_R01List: state.SYS2020_R01List.map((item) =>
			item.valueIndex === valueIndex ? data : item
		),
	})),

	deletedSYS2020_R01List: [],
	addDeletedSYS2020_R01List: (idList) => set((state) => {
		const deletedList = [...state.deletedSYS2020_R01List];
		for (const valueIndex of idList) {
			const isInserted = state.SYS2020_R01List.some(
				(item) => item.valueIndex === Number(valueIndex) && item.inserted,
			);
			const isModified = state.SYS2020_R01List.some(
				(item) => item.valueIndex === Number(valueIndex) && item.isModified,
			);
			if (!isInserted && !isModified) {
				deletedList.push(valueIndex);
			}
		}
		return { ...state, deletedSYS2020_R01List: deletedList };
	}),
	removeDeletedSYS2020_R01List: (idList) => set((state) => ({
		...state,
		deletedSYS2020_R01List: state.deletedSYS2020_R01List.filter(
			(value) => (idList ? !idList.includes(value) : false),
		),
	})),

	selectedSYS2020_R01List: [],
	setSelectedSYS2020_R01List: (idList) => set({ selectedSYS2020_R01List: idList }),
	addSelectedSYS2020_R01List: (idList) => set((state) => ({
		...state,
		selectedSYS2020_R01List: [...state.selectedSYS2020_R01List, ...idList],
	})),
	removeSelectedSYS2020_R01List: (idList) => set((state) => ({
		...state,
		selectedSYS2020_R01List: state.selectedSYS2020_R01List.filter(
			(value) => (idList ? !idList.includes(value) : false),
		),
	})),
	initSelectedSYS2020_R01List: () => set({ selectedSYS2020_R01List: [] }),
	allSelectedSYS2020_R01List: () => set((state) => ({
		...state,
		selectedSYS2020_R01List: state.SYS2020_R01List.map((item) => item.valueIndex.toString()),
	})),

	originalSYS2020_R01List: [],
	setOriginalSYS2020_R01List: (list) => set((state) => {
		let newList: SYS2020_R01Dto[] = [];
		let valueAndOrderIndex = 1;
		list.forEach((item) => {
			newList.push({
				...defaultSYS2020_R01Dto,
				...item,
				valueIndex: valueAndOrderIndex,
				orderIndex: valueAndOrderIndex,
				orderIndexSeq: valueAndOrderIndex,
			});
			valueAndOrderIndex++;
		});
		return { ...state, originalSYS2020_R01List: newList };
	}),

	columnState: null,
	setColumnState: (columnState) => set({ columnState }),
	scrollTop: null,
	setScrollTop: (scrollTop) => set({ scrollTop }),
	allCount: 0,
	setAllCount: (allCount) => set({ allCount }),

	initData: () => set({
		SYS2020_R01Filter: defaultSYS2020_R01Filter,
		SYS2020_R01PastFilter: defaultSYS2020_R01Filter,
		SYS2020_R01List: [],
		deletedSYS2020_R01List: [],
		selectedSYS2020_R01List: [],
		columnState: null,
		originalSYS2020_R01List: [],
		allCount: 0,
		scrollTop: null,
	}),
}));

export default useSYS2020_R01Store;
